import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { SIGNED_DOCUMENTS } from '../constants/signed-documents'
import { SignedDocumentType } from '../types'

const KEBAB_ERROR_KEY = /^[a-z][a-z0-9-]*$/

interface ErrorTextOptions {
    /** Key trong `signed_document_errors` khi không dịch được (mặc định: ký số thất bại) */
    fallback?: string
}

/**
 * Câu hiển thị cho lỗi chứng từ ký số.
 *
 * Mã lỗi kebab-case của backend: tra `invoice:signed_document_errors.<mã>` trước (bản dịch
 * riêng của module, có chèn tên chứng từ `{{document}}` / `{{Document}}`), rồi tới namespace
 * `axios-error` (lỗi chung như `invoice-not-found`, `forbidden`). Mã `misa-*` lạ → câu chung
 * "không kết nối được MISA". Còn lại (vd câu lỗi Go thô của dữ liệu cũ) → câu dự phòng,
 * không lộ chi tiết kỹ thuật cho user.
 */
export const useSignedDocumentErrorText = (type: SignedDocumentType) => {
    const { t } = useTranslation('invoice')
    const { t: tAxiosError } = useTranslation('axios-error')

    return useCallback(
        (message?: string | null, options?: ErrorTextOptions) => {
            const { i18nKey } = SIGNED_DOCUMENTS[type]
            const names = {
                document: t(`${i18nKey}.name`),
                Document: t(`${i18nKey}.name-capitalized`),
            }
            const raw = message?.trim() ?? ''
            if (KEBAB_ERROR_KEY.test(raw)) {
                const translated =
                    t(`signed_document_errors.${raw}`, {
                        ...names,
                        defaultValue: '',
                    }) || tAxiosError(raw, { defaultValue: '' })
                if (translated) return translated
            }
            if (/misa/i.test(raw)) {
                return t('signed_document_errors.misa-esign-request-failed')
            }
            return t(
                `signed_document_errors.${options?.fallback ?? 'sign-failed'}`,
                names
            )
        },
        [t, tAxiosError, type]
    )
}
